/**
 * @fileoverview Policy Simulator - ใช้สำหรับจำลอง (simulate) และตรวจสอบการอนุญาตตามนโยบายความปลอดภัย
 * @author IAAM Team
 * @version 1.0.0
 */

import { getRuleByPolicyDiagram } from "./convertPolicyStructure";
import { isWithinWorkingHours } from "./WorkingHours"
import { matchIPv4 } from "./IPFilter"

/**
 * ประเภทของ Resource Type
 * @type {('WEB'|'API'|'DATA')}
 * - WEB
 * - API
 * - DATA
 */
type ResourceType = 'WEB' | 'API' | 'DATA'

/**
 * Interface สำหรับ Resource
 * @interface Resource
 * @property {string} name - ชื่อของ Resource
 * @property {ResourceType} type - ประเภทของ Resource ได้แก่ WEB, API หรือ DATA
 * @property {any} param - พารามิเตอร์เพิ่มเติมของ Resource (เช่น path สำหรับ API)
 */
interface Resource {
    //   id: string
    name: string
    type: ResourceType
    param: any
}

/**
 * Interface สำหรับ Environment
 * @interface Environment
 * @property {string} name - ชื่อของ Environment
 * @property {ResourceType} type - ประเภทของ Environment ได้แก่ WEB, API หรือ DATA
 * @property {string} timestamp - เวลาที่ส่งคำขอ (ISO 8601 format)
 */
interface Environment {
    //   id: string
    name: string
    type: ResourceType
    timestamp: string
}

/**
 * Interface สำหรับคำขอนโยบาย (Policy Request)
 * @interface PolicyRequest
 * @property {string} id - รหัสประจำตัวของคำขอ
 * @property {any} [subject] - ข้อมูลผู้ใช้ที่ทำการขอ (optional)
 * @property {Resource} resource -  Resourceที่ขอการเข้าถึง
 * @property {string} action - การกระทำที่ต้องการทำ (เช่น READ, WRITE, DELETE)
 * @property {Object} environment -  Environmentของคำขอ
 * @property {Environment} [environment.resource] -  Resource ที่ส่งคำขอมา
 * @property {string} [environment.public_ip] - IP address ของผู้ส่ง
 */
interface PolicyRequest {
    id: string;
    subject?: any;
    resource: Resource;
    action: string;
    environment: {
        resource?: Environment;
        public_ip?: string
    };
}


/**
 * คลาส PolicySimulator - จำลองและตรวจสอบการอนุญาตตามนโยบายความปลอดภัย
 * 
 * @class PolicySimulator
 * @description จัดการการตรวจสอบและจำลองคำขอเข้าถึง Resourceตามนโยบายที่กำหนด
 * 
 * @example
 * const simulator = new PolicySimulator(policyDiagram);
 * const request = simulator.createPolicyRequest(
 *   'req-001',
 *   { name: 'api-resource', type: 'API', param: { path: '/users' } },
 *   'GET'
 * );
 * const result = await simulator.simulate(request, userProfile);
 */
class PolicySimulator {
    /** @type {any} - แผนภาพนโยบาย (Policy Diagram) */
    policyDiagram: any;

    /** @type {any[]} - รายการกฎนโยบายที่ประมวลผล */
    policies: any[];

    /**
     * สร้าง instance ของ PolicySimulator
     * 
     * @constructor
     * @param {any} policyDiagram
     * 
     * @description
     * - ตั้งค่า policyDiagram
     * - เริ่มต้นการโหลด Rules จาก policyDiagram (asynchronously)
     * - หากเกิดข้อผิดพลาดขณะโหลดกฎ จะตั้งค่า policies เป็นอาร์เรย์ว่าง
     */
    constructor(policyDiagram: any) {
        this.policyDiagram = policyDiagram;
        // ตั้งค่าเริ่มต้นสำหรับกฎ
        this.policies = [];
        // โหลดกฎจากแผนภาพนโยบายแบบ asynchronous
        getRuleByPolicyDiagram(this.policyDiagram).then((r: any) => {
            this.policies = r;
            console.log("rules:", r)
        }).catch(() => {
            // หากเกิดข้อผิดพลาด ให้ใช้ policies ว่าง
            this.policies = [];
        });
    }

    /**
     * สร้าง Policy Request
     * 
     * @method createPolicyRequest
     * @param {string} id - ID ของ Policy Request
     * @param {Resource} resource - Resource ที่ขอการเข้าถึง
     * @param {string} action - การกระทำที่ต้องการทำ (OPEN, GET, PUT, POST, DELETE เป็นต้น)
     * @param {Environment} [environment] -  Environment ของคำขอ (optional)
     * @param {string} [public_ip] - IP address สาธารณะของผู้ส่ง (optional)
     * 
     * @returns {PolicyRequest} - คำขอ (Policy Request)
     * 
     * @description
     * ใช้สำหรับสร้างและจัดรูปแบบคำขอนโยบาย
     * - รวมข้อมูล Environment ถ้ามี
     * - รวม IP address ถ้ามี
     */
    createPolicyRequest(id: string, resource: Resource, action: string, environment?: Environment, public_ip?: string): PolicyRequest {
        return {
            id: id,
            resource: resource,
            action: action,
            environment: {
                // รวม Environment (resource) ถ้ามีการระบุไว้
                ...(environment ? { resource: environment } : {}),
                // รวม IP address ถ้ามีการระบุไว้
                ...(public_ip ? { public_ip } : {})
            }
        }
    }


    /**
     * จำลองและตรวจสอบคำขอเข้าถึงตามนโยบาย (Policy Request)
     * 
     * @async
     * @method simulate
     * @param {PolicyRequest} policRequest - คำขอ (Policy Request)
     * @param {any} profile - ข้อมูลโปรไฟล์ผู้ใช้ (subject)
     * 
     * @returns {Promise<{id: string, decision: string, type?: string, message?: string}>}
     * - id: ID ของคำขอ
     * - decision: ผลการตัดสินใจ (Permit, Deny, Inapplicable)
     * - type: ประเภทของ Resource
     * - message: ข้อความรายละเอียด (ถ้ามี)
     * 
     * @description
     * ขั้นตอนการตรวจสอบ:
     * 1. รวมข้อมูลโปรไฟล์ (subject) เข้ากับคำขอ
     * 2. ค้นหานโยบายที่ตรงกับประเภทและชื่อ Resource
     * 3. ตรวจสอบข้อจำกัด subject (subject_constraint)
     * 4. ตรวจสอบ Environment (environment)
     * 5. ตรวจสอบการกระทำ (action)
     * 6. ตรวจสอบเวลาทำการ (working hours)
     * 7. ตรวจสอบ IP address
     * 8. คืนผลการตัดสินใจ
     * 
     * @throws หากเกิดข้อผิดพลาด จะคืนค่าผลการตัดสินใจ Denied พร้อมข้อความแสดงข้อผิดพลาด
     */
    async simulate(policRequest: PolicyRequest, profile: any) {
        try {
            // รวมข้อมูล subject (โปรไฟล์ผู้ใช้) เข้ากับคำขอ
            const request: PolicyRequest = { ...policRequest, subject: profile }

            // ค้นหานโยบายที่ประเภทและชื่อ Resourceตรงกับคำขอ
            const policy_matching = this.policies.filter((policy: any) => {
                return policy.resource?.type === request.resource.type && policy.resource?.name === request.resource.name
            })

            // console.log("founded policy:", policy_matching)

            // ถ้าไม่พบนโยบายที่ตรงกัน ให้ถือว่าไม่สามารถใช้ได้
            if (!policy_matching.length) throw { id: request.id, decision: "Inapplicable", message: "Policy mismatch" };

            let rule = undefined;
            // วนลูปเพื่อหานโยบายที่ตรงกับเงื่อนไขทั้งหมด
            for (const policy of policy_matching) {
                // ตรวจสอบข้อจำกัด subject (subject_constraint)
                // ถ้า subject_constraint เป็น null จะถือว่าตรงกัน
                // ถ้ามีค่า จะใช้ eval() เพื่อประเมินนิพจน์ (ต้องระมัดระวังการใช้ eval)
                if (policy?.subject_constraint === null || eval(policy.subject_constraint)) {

                    // ตรวจสอบการกระทำ (action) โดยเปรียบเทียบแบบ case-insensitive
                    const actions_uppercased = policy.action.map((action: string) => action.toUpperCase());
                    // console.log(actions_uppercased, request.action.toUpperCase())
                    if (!actions_uppercased.includes(request.action.toUpperCase())) throw { id: request.id, message: `Action: ${request.action} from '${request.environment?.resource?.type}:${request.environment?.resource?.name}' does not allow access.` };
                    else {
                        // ตรวจสอบ path สำหรับ API resources
                        if (request.resource.type.toUpperCase() === "API") {
                            if (policy.resource.param.path.endsWith("*")) {
                                // ใช้ wildcard base ของนโยบายเพื่อตรวจสอบ prefix
                                // ลบ /* ออกจาก path
                                const wildcardBase = policy.resource.param.path.slice(0, -2);
                                // console.log("wildcardBase:", wildcardBase)
                                // console.log("start with wildcardBase:", request.resource.param.path.startsWith(wildcardBase))
                                if (!request.resource.param.path.startsWith(wildcardBase)) throw { id: request.id, decision: "Inapplicable", message: `request '${request.resource.param.path}' mismatch` };
                                else {
                                    // ตรวจสอบ Environment
                                    // ถ้านโยบายไม่มีข้อจำกัด หรือ Environment ตรงกัน
                                    if (request.environment.resource?.name) {
                                        if (!policy.environment?.resources || policy.environment.resources.some((envResource: Environment) => envResource?.type === request.environment?.resource?.type && envResource?.name === request.environment?.resource?.name)) {
                                            rule = policy;
                                            break;
                                        }
                                    }
                                    else {
                                        rule = policy;
                                        break;
                                    }
                                }
                            }
                            // ตรวจสอบ path แบบตรงตัว (exact match)
                            else if (request.resource.param.path !== policy.resource.param.path) throw { id: request.id, decision: "Inapplicable", message: `request '${request.resource.param.path}' mismatch` };
                            else {
                                // ตรวจสอบ Environment
                                // ถ้านโยบายไม่มีข้อจำกัด หรือ Environment ตรงกัน
                                if (request.environment.resource?.name) {
                                    if (!policy.environment?.resources || policy.environment.resources.some((envResource: Environment) => envResource?.type === request.environment?.resource?.type && envResource?.name === request.environment?.resource?.name)) {
                                        rule = policy;
                                        break;
                                    }
                                }
                                else {
                                    rule = policy;
                                    break;
                                }
                            }
                        }
                        else {
                            // ตรวจสอบ Environment
                            // ถ้านโยบายไม่มีข้อจำกัด หรือ Environment ตรงกัน
                            if (request.environment.resource?.name) {
                                if (!policy.environment?.resources || policy.environment.resources.some((envResource: Environment) => envResource?.type === request.environment?.resource?.type && envResource?.name === request.environment?.resource?.name)) {
                                    rule = policy;
                                    break;
                                }
                            }
                            else {
                                rule = policy;
                                break;
                            }
                        }
                    }

                }
                else throw { id: request.id, message: "your request does not allow access." };
            }

            if(!rule && request.environment.resource?.name) throw { id: request.id, message: `the request from '${request.environment?.resource?.type}:${request.environment?.resource?.name}' does not allow access.` };

            // ถ้าไม่พบกฎที่ตรงกัน ให้ส่งคืน Inapplicable
            if (!rule) throw { id: request.id, decision: "Inapplicable", message: "Policy mismatch" };
            else {
                // สร้างผลการตัดสินใจเริ่มต้น
                let decision: { id: string, decision: string, type: string, message?: string } = {
                    "id": request.id,
                    "decision": rule.effect,
                    "type": rule.resource.type
                }

                // ตัวแปรสำหรับบันทึกผลการตรวจสอบเพิ่มเติม
                let allowWithTime = true;
                let allowWithIP = true;

                // ดึงข้อมูล Environmentจากนโยบาย
                const env = rule?.environment;

                // ตรวจสอบเวลาทำการ (working hours)
                if (env?.time) {
                    if (policRequest.environment.resource?.timestamp) {
                        const ts = new Date(policRequest.environment.resource.timestamp)
                        allowWithTime = isWithinWorkingHours(env.time, !isNaN(ts.getTime()) ? ts : undefined);
                    }
                    else allowWithTime = isWithinWorkingHours(env.time);
                }

                // ตรวจสอบ IP address
                if (env?.ip_address) {
                    allowWithIP = request.environment?.public_ip ? matchIPv4(request.environment.public_ip, env.ip_address.split(',')) : false;
                }

                // ถ้าไม่อนุญาตตามเวลาหรือ IP address ให้ปฏิเสธคำขอ
                if (!allowWithTime || !allowWithIP) {
                    decision['decision'] = "Denied"
                    if (!allowWithTime) {
                        decision['message'] = "Your request is not available during business hours. Please try again later.";
                    }
                    else {
                        decision['message'] = request.environment?.public_ip ? `Your public ip:${request.environment.public_ip} is not authorized.` : "public ip is not send from the client."
                    }
                }

                return decision;
            }
        } catch (error: any) {
            // จับข้อผิดพลาดทั้งหมดและคืนค่าเป็นผลการตัดสินใจ Denied
            return {
                id: error?.id ?? "",
                decision: error?.decision ?? "Denied",
                message: error?.message ?? String(error)
            }
        }
    }
}

/**
 * ส่งออก class PolicySimulator เพื่อใช้งานในส่วนอื่นของระบบ
 * @exports PolicySimulator
 */
export default PolicySimulator;

